<template>
  <div class="test-container nav-content-inset exam-take-page">
    <ImageBar :title="examTitle" height="80px" keep-title-on-mobile />
    <div class="test-content exam-take-content" v-loading="loading">
      <ExamMobileProgress
        v-if="questions && questions.length > 0"
        :current="currentIndex"
        :total="questions.length"
        :answers="answers"
        @jump="jumpToQuestion"
      />
      <div class="test-layout exam-take-body">
        <div
          v-if="questions && questions.length > 0"
          class="question-matrix question-matrix--desktop"
        >
          <template v-for="(_, index) in questions.length" :key="index">
            <div
              class="matrix-item"
              :class="{
                current: index === currentIndex,
                answered: answers && answers[index] !== null,
                'answered-not-current':
                  answers && answers[index] !== null && index !== currentIndex
              }"
              @click="jumpToQuestion(index)"
            >
              {{ index + 1 }}
            </div>
          </template>
        </div>

        <div class="question-main">
          <template v-if="currentQuestion">
            <div class="question-section">
              <div v-if="showQuestionTag" class="question-header">
                <h2 class="question-header__title">第 {{ currentIndex + 1 }} 题</h2>
                <div class="question-tag">{{ currentQuestion.question_tag }}</div>
              </div>
              <div v-else class="question-header question-header--desktop-only">
                <h2 class="question-header__title">第 {{ currentIndex + 1 }} 题</h2>
              </div>

              <div class="question-content" :key="`q-${currentIndex}`">
                <div class="question-card">
                  <p class="question-text">
                    <span class="question-index">{{ currentIndex + 1 }}.</span>
                    {{ currentQuestion.question }}
                  </p>
                </div>

                <div class="options-list" :class="questionToneClass">
                  <div
                    v-for="(option, index) in currentQuestion.options"
                    :key="`${currentQuestion.qid}-${option.oid ?? index}`"
                    class="option-item"
                    :class="{ selected: option.choose }"
                    @click="selectOption(index)"
                  >
                    <span class="option-radio" :class="{ selected: option.choose }">
                      <span v-if="option.choose" class="option-radio__check">✓</span>
                    </span>
                    <div class="option-content">
                      <span class="option-text">
                        <span
                          class="checkmark checkmark--desktop"
                          :class="{ animate: option.choose }"
                        >{{ option.choose ? '√' : ' ' }}</span>
                        {{ option.option }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- <el-alert
              v-if="!isLoggedIn"
              class="guest-hint-alert"
              type="info"
              :closable="false"
              show-icon
              title="可先完成测评；查看结果须登录。付费测评需购买后方可查看报告。"
            /> -->
            <div class="navigation-buttons exam-take-actions">
              <el-button type="primary" :disabled="currentIndex === 0" @click="prevQuestion">
                上一题
              </el-button>
              <el-button
                type="primary"
                :disabled="currentIndex === questions.length - 1"
                @click="nextQuestion"
              >
                下一题
              </el-button>
              <el-button
                type="success"
                class="exam-take-actions__submit"
                :disabled="!isAllAnswered"
                @click="submitTest"
              >
                {{ isLoggedIn ? '提交' : '提交并查看结果' }}
              </el-button>
            </div>
          </template>

          <div v-else class="empty-state">
            <el-empty description="未找到测试题目" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  isExamChargeable,
  formatExamFeeYuan,
  examFeeCents,
  getExamRouteKey,
  examDetailPath
} from '@/utils/examPricing'
import ImageBar from './components/ImageBar.vue'
import ExamMobileProgress from './components/exam/ExamMobileProgress.vue'
import { goToLogin, loginPagePath, isLoggedIn as hasValidLoginSession } from '@/utils/loginFlow'
import {
  saveGuestAnswers,
  claimGuestSession,
  redirectToLoginForClaim,
  getStoredSid,
  clearStoredSid,
  stashPendingGuestAnswers
} from '@/utils/guestExam'
import {
  stashGuestClaimContext,
  redirectForPaidExamClaim,
  isNotPurchasedClaimError
} from '@/utils/examClaim'
import {
  recordExamStart,
  getElapsedSeconds,
  stashElapsedSeconds,
  clearExamTiming
} from '@/utils/examTiming'

export default {
  name: 'TestView',
  components: {
    ImageBar,
    ExamMobileProgress
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const loading = ref(false)
    const questions = ref([])
    const currentIndex = ref(0)
    const answers = ref([])
    const examTitle = ref('')
    const examId = ref(null)
    const loadedExam = ref(null)
    const audio = ref(null)
    const isSelecting = ref(false)
    const autoSaveTimer = ref(null)

    const currentQuestion = computed(() => {
      return questions.value?.[currentIndex.value] || null
    })

    /** 四色按题序轮换：每切换一题换一色（0→1→2→3→0…） */
    const questionToneClass = computed(() => {
      const tone = currentIndex.value % 4
      return `question-tone-${tone}`
    })

    const isAllAnswered = computed(() => {
      return answers.value.length > 0 && answers.value.every(answer => answer !== null)
    })

    const isLoggedIn = computed(() => hasValidLoginSession())

    const showQuestionTag = computed(() => {
      const tag = currentQuestion.value?.question_tag
      return !!tag && tag !== 'default'
    })

    const formatAnswer = (answers) => {
      return answers.filter(answer => answer !== null).map(answer => ({
        qid: answer.qid,
        quest: answer.question_text,
        oid: answer.oid,
        option: answer.option_text
      }))
    }

    const saveAnswers = async (isTemp = true) => {
      try {
        const formattedAnswers = JSON.stringify(formatAnswer(answers.value))
        const token = localStorage.getItem('jwt_token')
        if (!hasValidLoginSession()) {
          const elapsed = getElapsedSeconds(examId.value)
          await saveGuestAnswers(examId.value, formattedAnswers, elapsed)
          return
        }

        const response = await axios.post('/api/v1/exam/answer', {
          examId: examId.value,
          answers: formattedAnswers,
          isTemp: isTemp ? 1 : 0,
          totalTime: isTemp ? 0 : getElapsedSeconds(examId.value)
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code !== 200) {
          if (response.data.code === 401) {
            ElMessage.error('请先登录')
            goToLogin(router, route.fullPath)
          } else if (!isTemp) {
            ElMessage.error(response.data.message || '提交失败')
          }
        } else if (!isTemp) {
          ElMessage.success('提交成功')
        }
      } catch (error) {
        console.error('Error saving answers:', error)
        if (!isTemp) {
          ElMessage.error('提交答案失败')
        }
      }
    }

    watch(currentIndex, (index) => {
      syncChooseForQuestion(index)
    })

    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    const promptSubmitOnLastQuestion = async () => {
      // 稍晚弹出，先让用户看到选中高亮
      await delay(450)

      const submitLabel = isLoggedIn.value ? '提交' : '提交并查看结果'
      const message = isAllAnswered.value
        ? '已完成全部题目，是否提交测评？'
        : '当前为最后一题，尚有题目未作答，确定要提交吗？'

      await ElMessageBox.confirm(message, '提交确认', {
        confirmButtonText: submitLabel,
        cancelButtonText: '再看看',
        type: 'info'
      })
      await submitTest()
    }

    const selectOption = async (optionIndex) => {
      if (isSelecting.value) return

      const question = currentQuestion.value
      if (!question) return

      isSelecting.value = true

      const option = question.options[optionIndex]

      question.options.forEach((opt) => {
        opt.choose = false
      })
      option.choose = true

      if (audio.value) {
        audio.value.currentTime = 0
        audio.value.play()
      }

      const answer = {
        oid: option.oid,
        qid: question.qid,
        question_key: question.question_key,
        question_tag: question.question_tag,
        question_score: question.score || 0,
        option_score: option.score || 0,
        option_index: optionIndex,
        option_text: option.option,
        question_text: question.question
      }
      answers.value[currentIndex.value] = answer

      saveAnswers(true)

      const isLastQuestion = currentIndex.value >= questions.value.length - 1
      if (isLastQuestion) {
        try {
          await promptSubmitOnLastQuestion()
        } catch {
          // 用户点「再看看」：保留当前选择，允许再次点击选项后重新弹出
        } finally {
          isSelecting.value = false
        }
        return
      }

      setTimeout(() => {
        currentIndex.value++
        setTimeout(() => {
          isSelecting.value = false
        }, 100)
      }, 250)
    }

    const prevQuestion = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--
      }
    }

    const nextQuestion = () => {
      if (currentIndex.value < questions.value.length - 1) {
        currentIndex.value++
      }
    }

    const jumpToQuestion = (index) => {
      if (index >= 0 && index < questions.value.length) {
        currentIndex.value = index
      }
    }

    const submitTest = async () => {
      try {
        loading.value = true
        const formattedAnswers = JSON.stringify(formatAnswer(answers.value))
        const totalTime = getElapsedSeconds(examId.value)
        if (!hasValidLoginSession()) {
          stashElapsedSeconds(examId.value, totalTime)
          stashPendingGuestAnswers(examId.value, formattedAnswers)
          const sid = await saveGuestAnswers(examId.value, formattedAnswers, totalTime)
          stashGuestClaimContext({ sid, examId: examId.value })
          const paid = isExamChargeable(loadedExam.value)
          ElMessage.info(
            paid
              ? '答题已保存，请先登录。该测评为付费内容，购买后方可查看报告。'
              : '答题已保存，请先登录后查看测评结果。'
          )
          router.push(redirectToLoginForClaim({ sid, examId: examId.value }))
          return
        }

        const sid = getStoredSid(examId.value)
        if (sid) {
          try {
            await saveGuestAnswers(examId.value, formattedAnswers, totalTime)
            const data = await claimGuestSession(sid, totalTime)
            clearStoredSid(examId.value)
            clearExamTiming(examId.value)
            ElMessage.success('报告已生成')
            router.push(`/exam/result/${data.resultId}`)
            return
          } catch (claimErr) {
            clearStoredSid(examId.value)
            if (claimErr.code === 'sid_expired' || claimErr.message?.includes('sid expired')) {
              // 过期 sid（常见于登出后残留），改走已登录正式提交
            } else if (isNotPurchasedClaimError(claimErr)) {
              redirectForPaidExamClaim(router, examId.value)
              return
            } else {
              console.error('claim guest session failed:', claimErr)
              ElMessage.error(claimErr.message || '绑定答题记录失败，请重试')
              return
            }
          }
        }

        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/exam/answer', {
          examId: examId.value,
          answers: formattedAnswers,
          isTemp: 0,
          totalTime
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          clearExamTiming(examId.value)
          ElMessage.success('测试提交成功')
          const resultId = response.data.data.resultId
          router.push(`/exam/result/${resultId}`)
        } else if (response.data.code === 401) {
          ElMessage.error('请先登录')
          goToLogin(router, route.fullPath)
        } else {
          ElMessage.error(response.data.message || '提交失败')
        }
      } catch (error) {
        console.error('Error submitting test:', error)
        ElMessage.error(error.message || '提交失败，请重试')
      } finally {
        loading.value = false
      }
    }

    // Auto-save every 30 seconds
    const startAutoSave = () => {
      autoSaveTimer.value = setInterval(() => {
        if (answers.value.some(answer => answer !== null)) {
          saveAnswers(true)
        }
      }, 30000)
    }

    const stopAutoSave = () => {
      if (autoSaveTimer.value) {
        clearInterval(autoSaveTimer.value)
        autoSaveTimer.value = null
      }
    }

    const checkExamPurchase = async () => {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(`/api/v1/exam/${encodeURIComponent(route.params.id)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          const exam = response.data.data
          
          // Check if exam is already purchased
          if (exam.isAcquired > 0) {
            // Already purchased, proceed to fetch questions
            return true
          }
          
          // Check if exam is chargeable (free=1 或 fee=0 均视为免费)
          if (isExamChargeable(exam)) {
            // Show confirmation dialog for paid exams
            try {
              await ElMessageBox.confirm(
                `确定要购买此测评吗？费用：¥${formatExamFeeYuan(exam)}`,
                '购买确认',
                {
                  confirmButtonText: '确定购买',
                  cancelButtonText: '取消',
                  type: 'info',
                }
              )
            } catch {
              router.push(examDetailPath({ id: exam.id, examSname: exam.examSname }))
              return false
            }

            try {
              // First check user balance
              const balanceResponse = await axios.get('/api/v1/user/balance', {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })

              if (balanceResponse.data.code === 200 && balanceResponse.data.data && balanceResponse.data.data.length > 0) {
                const userBalance = balanceResponse.data.data[0].balance // Balance in cents
                const examFee = examFeeCents(exam)
                
                if (userBalance < examFee) {
                  ElMessage.error(`余额不足，需要 ¥${(examFee / 100).toFixed(2)}，当前余额 ¥${(userBalance / 100).toFixed(2)}，请先充值`)
                  router.push('/recharge')
                  return false
                }
              }

              // Proceed with purchase
              const buyResponse = await axios.post('/api/v1/exam/buy', {
                id: parseInt(exam.id)
              }, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              })

              if (buyResponse.data.code === 200) {
                ElMessage.success('购买成功，开始测评')
                return true
              } else {
                ElMessage.error(buyResponse.data.message || '购买失败')
                router.push('/exam/list')
                return false
              }
            } catch (error) {
              console.error('Error buying exam:', error)
              if (error.response?.status === 401) {
                ElMessage.error('请先登录')
                goToLogin(router, route.fullPath)
              } else if (error.response?.status === 402) {
                ElMessage.error('余额不足，请先充值')
                router.push('/recharge')
              } else {
                ElMessage.error('购买失败，请重试')
              }
              router.push('/exam/list')
              return false
            }
          } else {
            // Free exam, proceed directly
            return true
          }
        } else if (response.data.code === 401) {
          const currentHrefWithoutDomain = window.location.href.replace(window.location.origin, '')
          console.log(currentHrefWithoutDomain)
          window.location.href = loginPagePath(currentHrefWithoutDomain)
          // ElMessage.error(response.data.message || '获取测试信息失败')
          // router.push('/exam/list')
          return false
        } else {
          ElMessage.error(response.data.message || '获取测试信息失败')
          router.push('/exam/list')
          return false
        }
      } catch (error) {
        console.error('Error checking exam purchase:', error)
        ElMessage.error('获取测试信息失败')
        router.push('/exam/list')
        return false
      }
    }

    /** 按 answers 同步指定题的 option.choose，避免切题后高亮错位 */
    const syncChooseForQuestion = (index) => {
      const question = questions.value[index]
      if (!question?.options) return

      question.options.forEach((opt) => {
        opt.choose = false
      })

      const saved = answers.value[index]
      if (!saved) return

      const matched = question.options.find((opt) => opt.oid === saved.oid)
      if (matched) matched.choose = true
    }

    const applyExamQuestions = (exam) => {
      loadedExam.value = exam
      examId.value = exam.id
      if (hasValidLoginSession()) {
        clearStoredSid(exam.id)
      }
      recordExamStart(exam.id)
      const key = getExamRouteKey(exam)
      if (key && String(route.params.id) !== String(key)) {
        router.replace({ name: 'Test', params: { id: key } })
      }
      examTitle.value = exam.examName || '测试'
      const raw = exam.questions || exam.originalQuestions || '{}'
      const parsedQuestions = JSON.parse(raw)
      questions.value = Array.isArray(parsedQuestions)
        ? parsedQuestions
        : (parsedQuestions.questions || [])
      answers.value = new Array(questions.value.length).fill(null)
      currentIndex.value = 0
      syncChooseForQuestion(0)
    }

    const fetchQuestionsGuest = async () => {
      try {
        loading.value = true
        const response = await axios.get(`/api/v1/exam/guest/${encodeURIComponent(route.params.id)}`)
        if (response.data.code === 200) {
          applyExamQuestions(response.data.data)
        } else {
          ElMessage.error(response.data.message || '获取测试题目失败')
          questions.value = []
          answers.value = []
          examTitle.value = '测试'
        }
      } catch (error) {
        console.error('Error fetching guest questions:', error)
        ElMessage.error('获取测试题目失败')
        questions.value = []
        answers.value = []
        examTitle.value = '测试'
      } finally {
        loading.value = false
      }
    }

    const fetchQuestions = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.get(`/api/v1/exam/${encodeURIComponent(route.params.id)}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.data.code === 200) {
          applyExamQuestions(response.data.data)
        } else {
          ElMessage.error(response.data.message || '获取测试题目失败')
          questions.value = []
          answers.value = []
          examTitle.value = '测试'
        }
      } catch (error) {
        console.error('Error fetching questions:', error)
        ElMessage.error('获取测试题目失败')
        questions.value = []
        answers.value = []
        examTitle.value = '测试'
      } finally {
        loading.value = false
      }
    }

    // const submitAnswer = async () => {
    //   try {
    //     loading.value = true
    //     const token = localStorage.getItem('jwt_token')
    //     const response = await axios.post('/api/v1/exam/answer', {
    //       examId: route.params.id,
    //       answer: JSON.stringify(answers.value),
    //       isTemp: 0
    //     }, {
    //       headers: {
    //         'Authorization': `Bearer ${token}`,
    //         'Content-Type': 'application/json'
    //       }
    //     })

    //     if (response.data.code === 200) {
    //       ElMessage.success('提交成功')
    //       router.push('/exam/list')
    //     } else if (response.data.code === 401) {
    //       ElMessage.error('请先登录')
    //       goToLogin(router)
    //     } else {
    //       ElMessage.error(response.data.message || '提交失败')
    //     }
    //   } catch (error) {
    //     console.error('Error submitting answer:', error)
    //     ElMessage.error('提交失败')
    //   } finally {
    //     loading.value = false
    //   }
    // }

    onMounted(async () => {
      audio.value = new Audio('/sounds/check.mp3')
      examId.value = route.params.id

      if (hasValidLoginSession()) {
        const canProceed = await checkExamPurchase()
        if (canProceed) {
          await fetchQuestions()
          startAutoSave()
        }
      } else {
        await fetchQuestionsGuest()
        startAutoSave()
      }
    })

    onUnmounted(() => {
      stopAutoSave()
    })

    return {
        loading,
        questions,
        currentIndex,
        currentQuestion,
        questionToneClass,
        selectOption,
        prevQuestion,
        nextQuestion,
        submitTest,
        jumpToQuestion,
        examTitle,
        examId,
        answers,
        isAllAnswered,
        isLoggedIn,
        showQuestionTag
      }
  }
}
</script>

<style scoped>
.test-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
}

.test-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.test-layout {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.question-matrix {
  flex: 0 0 200px;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  height: fit-content;
  margin-right: 20px;
}

.question-main {
  flex: 1;
  min-width: 0;
}

.matrix-item {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--el-border-color);
  border-radius: 50%;
  margin: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 13px;
  background-color: var(--el-bg-color);
}

.matrix-item:hover {
  border-color: var(--el-color-primary);
  background-color: var(--el-color-primary-light-9);
}

.matrix-item.current {
  border-color: var(--el-color-primary);
  background-color: var(--el-color-primary-light-9);
  font-weight: bold;
  transform: scale(1.1);
}

.matrix-item.answered {
  border-color: var(--el-color-success);
  background-color: var(--el-color-success-light-9);
}

.matrix-item.answered-not-current {
  border-color: var(--el-color-success);
  background-color: var(--el-color-success-light-8);
  opacity: 0.8;
}

.question-section {
  margin-bottom: 30px;
}


.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.question-header h2 {
  margin: 0;
  font-size: 24px;
  color: var(--el-text-color-primary);
}

.question-tag {
  padding: 4px 8px;
  background-color: var(--el-color-primary-light-9);
  color: var(--el-color-primary);
  border-radius: 4px;
  font-size: 14px;
}

.question-content {
  margin-bottom: 30px;
}

.question-text {
  font-size: 18px;
  color: var(--el-text-color-primary);
  margin-bottom: 20px;
  line-height: 1.6;
}

.question-index {
  font-weight: 700;
  margin-right: 4px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.option-item {
  padding: 15px;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  cursor: pointer;
  background-color: #fff;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}

/* 四色仅作用于选项按钮，按题序轮换，色调偏淡 */
.options-list.question-tone-0 .option-item {
  border-color: #e3ebe6;
  background-color: #fafcfb;
}

.options-list.question-tone-0 .option-item.selected {
  border-color: #b8d9c4;
  background-color: #f2f8f4;
}

.options-list.question-tone-1 .option-item {
  border-color: #ebe4dc;
  background-color: #fdfbf8;
}

.options-list.question-tone-1 .option-item.selected {
  border-color: #e8d0b0;
  background-color: #faf5ef;
}

.options-list.question-tone-2 .option-item {
  border-color: #dfe6f0;
  background-color: #fafbfd;
}

.options-list.question-tone-2 .option-item.selected {
  border-color: #b8cce8;
  background-color: #f3f7fc;
}

.options-list.question-tone-3 .option-item {
  border-color: #e8e0f0;
  background-color: #fcfafd;
}

.options-list.question-tone-3 .option-item.selected {
  border-color: #d4c0e8;
  background-color: #f8f4fc;
}

@media (hover: hover) {
  .options-list.question-tone-0 .option-item:not(.selected):hover {
    border-color: #c5d9cc;
    background-color: #f5faf7;
  }

  .options-list.question-tone-1 .option-item:not(.selected):hover {
    border-color: #e0d0c0;
    background-color: #fcf8f4;
  }

  .options-list.question-tone-2 .option-item:not(.selected):hover {
    border-color: #c5d4e8;
    background-color: #f6f9fd;
  }

  .options-list.question-tone-3 .option-item:not(.selected):hover {
    border-color: #d0c0e0;
    background-color: #faf7fc;
  }
}

.options-list .option-item.selected .checkmark {
  color: var(--el-text-color-primary);
}

.option-content {
  display: flex;
  align-items: center;
}

.option-text {
  font-size: 16px;
  color: var(--el-text-color-regular);
  display: flex;
  align-items: center;
}

.checkmark {
  color: var(--el-color-primary);
  margin-right: 8px;
  font-weight: bold;
  min-width: 1em;
  display: inline-block;
  text-align: center;
  opacity: 0;
  transform: scale(0);
  transition: all 0.3s ease-out;
}

.checkmark.animate {
  opacity: 1;
  transform: scale(1);
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.question-header--desktop-only .question-header__title {
  margin: 0;
}

.empty-state {
  padding: 40px 0;
  text-align: center;
}

@media (max-width: 767px) {
  .question-header--desktop-only {
    display: none;
  }
}
</style>

<!-- 不加 scoped：切题 animation 在 iOS / Android WebView 上更稳定 -->
<style>
@keyframes test-question-in {
  from {
    opacity: 0;
    transform: translate3d(0, 14px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@-webkit-keyframes test-question-in {
  from {
    opacity: 0;
    -webkit-transform: translate3d(0, 14px, 0);
  }
  to {
    opacity: 1;
    -webkit-transform: translate3d(0, 0, 0);
  }
}

.test-container .question-content {
  animation: test-question-in 0.3s ease-out both;
  -webkit-animation: test-question-in 0.3s ease-out both;
}

@media (max-width: 767px) {
  .test-container .question-content {
    animation-duration: 0.34s;
    -webkit-animation-duration: 0.34s;
  }
}

@media (prefers-reduced-motion: reduce) {
  .test-container .question-content {
    animation: none;
    -webkit-animation: none;
  }
}
</style>